import { nameOriginLabel } from "@szzt/core";
import { parseAtUri, validateRouteRecord } from "@szzt/records";

declare const __SZZT_ROUTE_COLLECTION__: string;
export const ROUTE_COLLECTION = typeof __SZZT_ROUTE_COLLECTION__ === "undefined" ? "" : __SZZT_ROUTE_COLLECTION__;

export function namedSiteUrl(did: string, name: string, apex: string): string | null {
  const label = nameOriginLabel(did, name);
  return label === null ? null : `https://${label}.${apex}/`;
}

/** Visit prefers a supported persisted name; stable identity URLs remain the
 * fallback and should still be used for identity-bound operations like exports.
 * Multiple names have no primary flag, so choose deterministically by name. */
export function preferredVisitUrl(
  did: string | undefined, names: readonly string[], apex: string, stableUrl: string | null,
): string | null {
  if (did !== undefined) {
    for (const name of [...names].sort()) {
      const url = namedSiteUrl(did, name, apex);
      if (url !== null) return url;
    }
  }
  return stableUrl;
}

/** PDS-reported management display, not a verification claim. Unsupported routes
 * are not presented as working HTTPS names. Multiple names per site are retained. */
export function siteNames(
  records: readonly { uri: string; value: unknown }[], did: string, collection: string,
): Map<string, string[]> {
  const names = new Map<string, string[]>();
  for (const record of records) {
    try {
      const at = parseAtUri(record.uri);
      if (at.did !== did || at.collection !== collection || nameOriginLabel(did, at.rkey) === null) continue;
      const route = validateRouteRecord(record.value, collection);
      if (route.status === "retired" || route.alias !== undefined || route.targets?.length !== 1 ||
          (route.policy !== undefined && Object.keys(route.policy).length > 0)) continue;
      const target = parseAtUri(route.targets[0]!.uri);
      if (target.did !== did || target.collection !== "app.szzt.site") continue;
      const list = names.get(target.rkey) ?? [];
      list.push(at.rkey);
      names.set(target.rkey, list);
    } catch { /* malformed/unsupported route: not an HTTPS site name */ }
  }
  for (const list of names.values()) list.sort();
  return names;
}
